import React, { useState, useEffect } from "react";
import { useForm, Controller, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { ClientType } from "../../types";
import XanoAPI from "../../lib/XanoAPI";
import { useAuth } from "../../lib/AuthContext";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  FormControl,
  FormMessage,
} from "../ui/form";
import { useToast } from "../ui/use-toast";
import { Users, Loader2 } from "lucide-react";
import { useQueryClient, QueryClientProvider, useQueryClient as useQueryClientHook } from "@tanstack/react-query";

interface ReassignClientModalProps {
  client?: ClientType;
  clients?: ClientType[];
  trigger?: React.ReactNode;
  onSuccess?: () => void;
}

// Form validation schema
const reassignClientSchema = z.object({
  targetUserId: z.number().min(1, "Please select an agent"),
});

type ReassignClientFormData = z.infer<typeof reassignClientSchema>;

// Internal component that uses QueryClient
function ReassignClientModalInternal({
  client,
  clients,
  trigger,
  onSuccess,
}: ReassignClientModalProps) {
  const auth = useAuth();
  const api = XanoAPI.getInstance();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Get team members from user_agency structure
  const currentUser = auth?.currentUser;
  const userAgency = currentUser?.user_agency?.[0];
  const agencyMembers = userAgency?.members || [];
  const isAdmin = currentUser?.user_permissions?.[0]?.role === "admin";

  // Transform agency members to options
  interface AgentOption {
    id: number;
    name: string;
    email: string;
    role: "admin" | "agent";
  }

  const availableAgents: AgentOption[] = agencyMembers.map((member: any) => ({
    id: parseInt(member.id),
    name: member.name,
    email: member.email,
    role: userAgency?.agency_creator === parseInt(member.id) ? "admin" : "agent",
  }));

  // Determine which clients to reassign
  const clientsToReassign = client ? [client] : clients || [];
  const isBulkReassign = clientsToReassign.length > 1;

  const methods = useForm<ReassignClientFormData>({
    resolver: zodResolver(reassignClientSchema),
    mode: "onChange",
    defaultValues: {
      targetUserId: 0,
    },
  });

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors, isValid },
  } = methods;

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      reset({ targetUserId: 0 });
    }
  }, [isOpen, reset]);

  const onSubmit = async (data: ReassignClientFormData) => {
    if (clientsToReassign.length === 0) {
      toast({
        title: "Error",
        description: "No clients selected for reassignment.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      let successCount = 0;
      let errorCount = 0;
      const errors: string[] = [];

      // Reassign each client
      for (const clientToReassign of clientsToReassign) {
        if (!clientToReassign.id) {
          errorCount++;
          errors.push(`${clientToReassign.firstName || "Client"} ${clientToReassign.lastName || ""}: Missing client ID`);
          continue;
        }

        try {
          await api.reassignClient(clientToReassign.id, data.targetUserId);
          successCount++;
        } catch (error: any) {
          errorCount++;
          const clientName = `${clientToReassign.firstName || ""} ${clientToReassign.lastName || ""}`.trim() || `Client ${clientToReassign.id}`;
          errors.push(`${clientName}: ${error?.message || "Failed to reassign"}`);
        }
      }

      // Show results
      if (successCount > 0 && errorCount === 0) {
        toast({
          title: "Success",
          description: isBulkReassign
            ? `Successfully reassigned ${successCount} client${successCount > 1 ? "s" : ""} to the selected agent.`
            : `Successfully reassigned client to the selected agent.`,
        });
        setIsOpen(false);
        
        // Refresh table data if QueryClient is available
        if (queryClient) {
          try {
            queryClient.invalidateQueries({ queryKey: ['clients'] });
          } catch (error) {
            // QueryClientProvider not available, skip invalidation
            console.warn("QueryClientProvider not available, skipping query invalidation");
          }
        }
        
        if (onSuccess) {
          onSuccess();
        }
      } else if (successCount > 0 && errorCount > 0) {
        toast({
          title: "Partial Success",
          description: `Reassigned ${successCount} client${successCount > 1 ? "s" : ""}, but ${errorCount} failed.`,
          variant: "destructive",
        });
        console.error("Reassignment errors:", errors);
        
        // Refresh table data even on partial success if QueryClient is available
        if (queryClient) {
          try {
            queryClient.invalidateQueries({ queryKey: ['clients'] });
          } catch (error) {
            // QueryClientProvider not available, skip invalidation
            console.warn("QueryClientProvider not available, skipping query invalidation");
          }
        }
        
        if (onSuccess) {
          onSuccess();
        }
      } else {
        toast({
          title: "Error",
          description: `Failed to reassign ${errorCount} client${errorCount > 1 ? "s" : ""}.`,
          variant: "destructive",
        });
        console.error("Reassignment errors:", errors);
      }
    } catch (error: any) {
      console.error("❌ Error reassigning client(s):", error);
      toast({
        title: "Error",
        description: error?.message || "An unexpected error occurred while reassigning clients.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Don't render if user is not admin
  if (!isAdmin) {
    return null;
  }

  const selectedAgent = availableAgents.find(
    (agent) => agent.id === methods.watch("targetUserId")
  );

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="outline" size="sm">
            <Users className="h-4 w-4 mr-2" />
            Reassign
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {isBulkReassign
              ? `Reassign ${clientsToReassign.length} Clients`
              : `Reassign ${client?.firstName || ""} ${client?.lastName || ""}`.trim()}
          </DialogTitle>
          <DialogDescription>
            {isBulkReassign
              ? `Select an agent to reassign ${clientsToReassign.length} selected clients to.`
              : "Select an agent to reassign this client to."}
          </DialogDescription>
        </DialogHeader>

        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {/* Agent Selection */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-gray-600" />
                <Label htmlFor="targetUserId" className="text-sm font-semibold text-gray-700">
                  Assign to Agent <span className="text-red-500">*</span>
                </Label>
              </div>
              <FormControl>
                <Controller
                  name="targetUserId"
                  control={control}
                  render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <>
                      <Select
                        onValueChange={(val) => onChange(Number(val))}
                        value={value ? String(value) : ""}
                        disabled={isLoading}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Select an agent" />
                        </SelectTrigger>
                        <SelectContent>
                          {availableAgents.length === 0 ? (
                            <SelectItem value="" disabled>
                              No agents available
                            </SelectItem>
                          ) : (
                            availableAgents.map((agent) => (
                              <SelectItem key={agent.id} value={String(agent.id)}>
                                <div className="flex items-center gap-2">
                                  <span>{agent.name}</span>
                                  {agent.role === "admin" && (
                                    <span className="text-xs text-gray-500">(Admin)</span>
                                  )}
                                </div>
                              </SelectItem>
                            ))
                          )}
                        </SelectContent>
                      </Select>
                      {error && (
                        <FormMessage className="mt-1">{error.message}</FormMessage>
                      )}
                      {selectedAgent && !error && (
                        <p className="text-xs text-gray-500 mt-1">
                          Selected: {selectedAgent.name} {selectedAgent.email && `(${selectedAgent.email})`}
                        </p>
                      )}
                    </>
                  )}
                />
              </FormControl>
            </div>

            {/* Client List Preview (for bulk reassign) */}
            {isBulkReassign && clientsToReassign.length > 0 && (
              <div className="space-y-2">
                <Label className="text-sm font-semibold text-gray-700">
                  Clients to Reassign ({clientsToReassign.length})
                </Label>
                <div className="max-h-40 overflow-y-auto border rounded-md p-3 bg-gray-50">
                  <ul className="space-y-1">
                    {clientsToReassign.slice(0, 10).map((c) => (
                      <li key={c.id} className="text-sm text-gray-600">
                        {c.firstName} {c.lastName}
                        {c.user_id && (
                          <span className="text-xs text-gray-400 ml-2">
                            (Currently: {availableAgents.find((a: AgentOption) => a.id === c.user_id)?.name || `Agent ${c.user_id}`})
                          </span>
                        )}
                      </li>
                    ))}
                    {clientsToReassign.length > 10 && (
                      <li className="text-sm text-gray-500 italic">
                        ...and {clientsToReassign.length - 10} more
                      </li>
                    )}
                  </ul>
                </div>
              </div>
            )}

            {/* Current Assignment (for single client) */}
            {!isBulkReassign && client && client.user_id && (
              <div className="space-y-2">
                <Label className="text-sm font-semibold text-gray-700">
                  Current Assignment
                </Label>
                <p className="text-sm text-gray-600">
                  {availableAgents.find((a: AgentOption) => a.id === client.user_id)?.name || `Agent ${client.user_id}`}
                </p>
              </div>
            )}

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsOpen(false)}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={!isValid || isLoading || availableAgents.length === 0}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Reassigning...
                  </>
                ) : (
                  <>
                    <Users className="h-4 w-4 mr-2" />
                    Reassign
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </FormProvider>
      </DialogContent>
    </Dialog>
  );
}

// Error Boundary to catch QueryClient errors
class QueryClientErrorBoundary extends React.Component<
  { children: React.ReactNode; fallback: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode; fallback: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: any) {
    if (error?.message?.includes("QueryClient")) {
      return { hasError: true };
    }
    return null;
  }

  componentDidCatch(error: any, errorInfo: any) {
    if (error?.message?.includes("QueryClient")) {
      console.warn("QueryClientProvider not available, using fallback component");
    }
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback;
    }
    return this.props.children;
  }
}

// Wrapper component that safely handles QueryClient
export function ReassignClientModal(props: ReassignClientModalProps) {
  return (
    <QueryClientErrorBoundary
      fallback={<ReassignClientModalWithoutQueryClient {...props} />}
    >
      <ReassignClientModalInternal {...props} />
    </QueryClientErrorBoundary>
  );
}

// Fallback component without QueryClient
function ReassignClientModalWithoutQueryClient({
  client,
  clients,
  trigger,
  onSuccess,
}: ReassignClientModalProps) {
  const auth = useAuth();
  const api = XanoAPI.getInstance();
  const { toast } = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  
  // Get team members from user_agency structure
  const currentUser = auth?.currentUser;
  const userAgency = currentUser?.user_agency?.[0];
  const agencyMembers = userAgency?.members || [];
  const isAdmin = currentUser?.user_permissions?.[0]?.role === "admin";

  // Transform agency members to options
  interface AgentOption {
    id: number;
    name: string;
    email: string;
    role: "admin" | "agent";
  }

  const availableAgents: AgentOption[] = agencyMembers.map((member: any) => ({
    id: parseInt(member.id),
    name: member.name,
    email: member.email,
    role: userAgency?.agency_creator === parseInt(member.id) ? "admin" : "agent",
  }));

  // Determine which clients to reassign
  const clientsToReassign = client ? [client] : clients || [];
  const isBulkReassign = clientsToReassign.length > 1;

  const methods = useForm<ReassignClientFormData>({
    resolver: zodResolver(reassignClientSchema),
    mode: "onChange",
    defaultValues: {
      targetUserId: 0,
    },
  });

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors, isValid },
  } = methods;

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      reset({ targetUserId: 0 });
    }
  }, [isOpen, reset]);

  const onSubmit = async (data: ReassignClientFormData) => {
    if (clientsToReassign.length === 0) {
      toast({
        title: "Error",
        description: "No clients selected for reassignment.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      let successCount = 0;
      let errorCount = 0;
      const errors: string[] = [];

      // Reassign each client
      for (const clientToReassign of clientsToReassign) {
        if (!clientToReassign.id) {
          errorCount++;
          errors.push(`${clientToReassign.firstName || "Client"} ${clientToReassign.lastName || ""}: Missing client ID`);
          continue;
        }

        try {
          await api.reassignClient(clientToReassign.id, data.targetUserId);
          successCount++;
        } catch (error: any) {
          errorCount++;
          const clientName = `${clientToReassign.firstName || ""} ${clientToReassign.lastName || ""}`.trim() || `Client ${clientToReassign.id}`;
          errors.push(`${clientName}: ${error?.message || "Failed to reassign"}`);
        }
      }

      // Show results (without query invalidation)
      if (successCount > 0 && errorCount === 0) {
        toast({
          title: "Success",
          description: isBulkReassign
            ? `Successfully reassigned ${successCount} client${successCount > 1 ? "s" : ""} to the selected agent.`
            : `Successfully reassigned client to the selected agent.`,
        });
        setIsOpen(false);
        
        if (onSuccess) {
          onSuccess();
        }
      } else if (successCount > 0 && errorCount > 0) {
        toast({
          title: "Partial Success",
          description: `Reassigned ${successCount} client${successCount > 1 ? "s" : ""}, but ${errorCount} failed.`,
          variant: "destructive",
        });
        console.error("Reassignment errors:", errors);
        
        if (onSuccess) {
          onSuccess();
        }
      } else {
        toast({
          title: "Error",
          description: `Failed to reassign ${errorCount} client${errorCount > 1 ? "s" : ""}.`,
          variant: "destructive",
        });
        console.error("Reassignment errors:", errors);
      }
    } catch (error: any) {
      console.error("❌ Error reassigning client(s):", error);
      toast({
        title: "Error",
        description: error?.message || "An unexpected error occurred while reassigning clients.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const selectedAgent = availableAgents.find((a: AgentOption) => a.id === methods.watch("targetUserId"));

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="outline" size="sm">
            <Users className="h-4 w-4 mr-2" />
            Reassign
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {isBulkReassign
              ? `Reassign ${clientsToReassign.length} Clients`
              : `Reassign ${client?.firstName || ""} ${client?.lastName || ""}`.trim()}
          </DialogTitle>
          <DialogDescription>
            {isBulkReassign
              ? `Select an agent to reassign ${clientsToReassign.length} selected clients to.`
              : "Select an agent to reassign this client to."}
          </DialogDescription>
        </DialogHeader>

        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {/* Agent Selection */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-gray-600" />
                <Label htmlFor="targetUserId" className="text-sm font-semibold text-gray-700">
                  Assign to Agent <span className="text-red-500">*</span>
                </Label>
              </div>
              <FormControl>
                <Controller
                  name="targetUserId"
                  control={control}
                  render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <>
                      <Select
                        onValueChange={(val) => onChange(Number(val))}
                        value={value ? String(value) : ""}
                        disabled={isLoading}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Select an agent" />
                        </SelectTrigger>
                        <SelectContent>
                          {availableAgents.length === 0 ? (
                            <SelectItem value="" disabled>
                              No agents available
                            </SelectItem>
                          ) : (
                            availableAgents.map((agent) => (
                              <SelectItem key={agent.id} value={String(agent.id)}>
                                <div className="flex items-center gap-2">
                                  <span>{agent.name}</span>
                                  {agent.role === "admin" && (
                                    <span className="text-xs text-gray-500">(Admin)</span>
                                  )}
                                </div>
                              </SelectItem>
                            ))
                          )}
                        </SelectContent>
                      </Select>
                      {error && (
                        <FormMessage className="mt-1">{error.message}</FormMessage>
                      )}
                      {selectedAgent && !error && (
                        <p className="text-xs text-gray-500 mt-1">
                          Selected: {selectedAgent.name} {selectedAgent.email && `(${selectedAgent.email})`}
                        </p>
                      )}
                    </>
                  )}
                />
              </FormControl>
            </div>

            {/* Client List Preview (for bulk reassign) */}
            {isBulkReassign && clientsToReassign.length > 0 && (
              <div className="space-y-2">
                <Label className="text-sm font-semibold text-gray-700">
                  Clients to Reassign ({clientsToReassign.length})
                </Label>
                <div className="max-h-40 overflow-y-auto border rounded-md p-3 bg-gray-50">
                  <ul className="space-y-1">
                    {clientsToReassign.slice(0, 10).map((c) => (
                      <li key={c.id} className="text-sm text-gray-600">
                        {c.firstName} {c.lastName}
                        {c.user_id && (
                          <span className="text-xs text-gray-400 ml-2">
                            (Currently: {availableAgents.find((a: AgentOption) => a.id === c.user_id)?.name || `Agent ${c.user_id}`})
                          </span>
                        )}
                      </li>
                    ))}
                    {clientsToReassign.length > 10 && (
                      <li className="text-sm text-gray-500 italic">
                        ...and {clientsToReassign.length - 10} more
                      </li>
                    )}
                  </ul>
                </div>
              </div>
            )}

            {/* Current Assignment (for single client) */}
            {!isBulkReassign && client && client.user_id && (
              <div className="space-y-2">
                <Label className="text-sm font-semibold text-gray-700">
                  Current Assignment
                </Label>
                <p className="text-sm text-gray-600">
                  {availableAgents.find((a: AgentOption) => a.id === client.user_id)?.name || `Agent ${client.user_id}`}
                </p>
              </div>
            )}

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsOpen(false)}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={!isValid || isLoading || availableAgents.length === 0}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Reassigning...
                  </>
                ) : (
                  <>
                    <Users className="h-4 w-4 mr-2" />
                    Reassign
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </FormProvider>
      </DialogContent>
    </Dialog>
  );
}

